import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useApiResource } from '../hooks/useApiResource';
import { DetailPageLayout, DetailPageState } from '../components/DetailPageLayout';
import { getProjectIndustryLabel, getProjectStageLabel, getProjectStatusLabel } from '../lib/contentLabels';

export default function ProjectDetail() {
  const { slug } = useParams();
  const { i18n } = useTranslation();
  const { data: project, loading, error } = useApiResource(`/projects/${slug}`, { lang: i18n.language });

  const getLocalizedField = (item, field) => {
    if (i18n.language === 'en' && item[`${field}_en`]) {
      return item[`${field}_en`];
    }
    return item[field];
  };

  if (loading) {
    return <DetailPageState>Loading...</DetailPageState>;
  }

  if (!project) {
    return <DetailPageState>{error ? 'Failed to load project.' : 'Project not found.'}</DetailPageState>;
  }

  const labels = i18n.language === 'en'
    ? {
      back: 'Back to projects',
      stage: 'Stage',
      industry: 'Industry',
      country: 'Country',
      capital: 'Capital Required',
      timeline: 'Timeline',
      status: 'Status',
    }
    : {
      back: 'Назад к проектам',
      stage: 'Стадия',
      industry: 'Индустрия',
      country: 'Страна',
      capital: 'Требуемый капитал',
      timeline: 'Сроки',
      status: 'Статус',
    };
  const stageLabel = getProjectStageLabel(project.stage, i18n.language);
  const industryLabel = getProjectIndustryLabel(project.industry, i18n.language);
  const statusLabel = getProjectStatusLabel(project.status, i18n.language);

  return (
    <DetailPageLayout backTo="/invest" backLabel={labels.back}>
        <div className="glass detail-page-card">
          <div className="relative h-72 md:h-96">
            <img src={project.image_url} alt={getLocalizedField(project, 'title')} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
          </div>
          <div className="p-8 md:p-10 space-y-6">
            <div className="flex flex-wrap items-center gap-4 text-sm text-white/60">
              <span className="text-xs uppercase tracking-widest text-[#E11D2E]">{stageLabel}</span>
              <span>{industryLabel}</span>
              <span>{project.country}</span>
            </div>
            <h1 className="detail-page-title">
              {getLocalizedField(project, 'title')}
            </h1>
            <p className="detail-page-body whitespace-pre-line">
              {getLocalizedField(project, 'description')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="glass rounded-2xl p-4">
                <div className="text-xs uppercase tracking-widest text-[#E11D2E] mb-2">{labels.capital}</div>
                <div className="text-white font-semibold">{project.capital_required}</div>
              </div>
              <div className="glass rounded-2xl p-4">
                <div className="text-xs uppercase tracking-widest text-[#E11D2E] mb-2">{labels.timeline}</div>
                <div className="text-white font-semibold">{project.timeline}</div>
              </div>
              <div className="glass rounded-2xl p-4">
                <div className="text-xs uppercase tracking-widest text-[#E11D2E] mb-2">{labels.status}</div>
                <div className="text-white font-semibold">{statusLabel}</div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="glass rounded-2xl p-4">
                <div className="text-xs uppercase tracking-widest text-[#E11D2E] mb-2">{labels.stage}</div>
                <div className="text-white font-semibold">{stageLabel}</div>
              </div>
              <div className="glass rounded-2xl p-4">
                <div className="text-xs uppercase tracking-widest text-[#E11D2E] mb-2">{labels.industry}</div>
                <div className="text-white font-semibold">{industryLabel}</div>
              </div>
              <div className="glass rounded-2xl p-4">
                <div className="text-xs uppercase tracking-widest text-[#E11D2E] mb-2">{labels.country}</div>
                <div className="text-white font-semibold">{project.country}</div>
              </div>
            </div>
          </div>
        </div>
    </DetailPageLayout>
  );
}
